import React, { useState, useEffect, useMemo } from "react";

import PanelTemplate from "../../templates/Panel";
import Table from "../../components/Table";
import Box from "../../components/Box";
import PageHeader from "../../components/PageHeader";
import FilterSimple from "../../components/FilterSimple";
import { renderError } from "../../helpers/errors";
import api from "../../services/api";
import moment from "moment";
import { convertIntToMoney } from "../../helpers/conversions";
import DotStatus from "../../components/DotStatus";
import UserCell from "../../components/UserCell";
import Button from "../../components/Button";
import Modal from "../../components/Modal";
import { ModalFilter } from "./modals/ModalFilter";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { hasPermission } from "../../services/auth";
import { ModalDelete } from "../../components/ModalDelete";
import Pagination from "../../components/Pagination";
import { countOrderPendingConferencesTag } from "./helpers/conference";
import { ModalExport } from "../../components/ModalExport";
import { ModalEditStatus } from "../../components/ModalEditStatus";
import { toast } from "react-toastify";

const orderSearchParams = [
    {name: 'Nome da empresa', key: '[customer_company_trade_name]', operation: 'contain', type: 'text' },
    {name: 'Razão social', key: '[customer_company_company_name]', operation: 'contain', type: 'text' },
    {name: 'E-mail do contato', key: '[customer_email]', operation: 'contain', type: 'text' },
    {name: 'Código do pedido', key: '[code]', operation: 'equal', type: 'text' }
];

const getOrderSearchParam = field => (
    orderSearchParams.find(param => param.key === field) || orderSearchParams[0]
);

const normalizePage = page => {
    const parsedPage = parseInt(page, 10);
    return Number.isNaN(parsedPage) || parsedPage < 1 ? 1 : parsedPage;
};

const parseOrderListFilters = search => {
    const params = new URLSearchParams(search);
    const searchParam = getOrderSearchParam(params.get('search_field'));
    const statusIds = params.get('statuses');

    return {
        page: normalizePage(params.get('page')),
        status: params.get('status') || null,
        statuses: statusIds ? statusIds.split(',').filter(Boolean) : [],
        searchField: searchParam.key,
        searchOperation: params.get('search_operation') || searchParam.operation || 'contain',
        search: params.get('search') || '',
        adminId: params.get('admin_id') || null,
        createdFrom: params.get('created_from') || null,
        createdTo: params.get('created_to') || null,
    };
};

const normalizeStartDate = value => {
    if (!value) return null;
    return value.includes(':') ? value : `${moment(value).format('YYYY-MM-DD')} 00:00:00`;
};

const normalizeEndDate = value => {
    if (!value) return null;
    return value.includes(':') ? value : `${moment(value).format('YYYY-MM-DD')} 23:59:59`;
};

const buildOrderApiQuery = filters => {
    const params = new URLSearchParams();

    if (filters.search.trim() !== '') {
        params.append(`filters[0]${filters.searchField}[${filters.searchOperation}]`, filters.search.trim());
    }

    if (filters.statuses.length > 0) {
        params.append('status_ids', filters.statuses.join(','));
    } else if (filters.status) {
        params.append('filters[0][related][status.id][equal]', filters.status);
    }

    if (filters.adminId) {
        params.append('filters[0][admin_id][equal]', filters.adminId);
    }

    if (filters.createdFrom) {
        params.append('filters[0][created_at][bigger_or_equal]', normalizeStartDate(filters.createdFrom));
    }

    if (filters.createdTo) {
        params.append('filters[0][created_at][less_or_equal]', normalizeEndDate(filters.createdTo));
    }

    params.append('page', filters.page);

    return params.toString();
};

const buildOrderListSearch = filters => {
    const params = new URLSearchParams();

    if (filters.page > 1) params.set('page', filters.page);
    if (filters.status) params.set('status', filters.status);
    if (filters.statuses.length > 0) params.set('statuses', filters.statuses.join(','));
    if (filters.search.trim() !== '') {
        params.set('search_field', filters.searchField);
        params.set('search_operation', filters.searchOperation);
        params.set('search', filters.search.trim());
    }
    if (filters.adminId) params.set('admin_id', filters.adminId);
    if (filters.createdFrom) params.set('created_from', filters.createdFrom);
    if (filters.createdTo) params.set('created_to', filters.createdTo);

    return params.toString();
};

const hasActiveValue = value => value !== null && value !== undefined && value !== '';

const countActiveOrderFilters = filters => ([
    filters.search.trim(),
    filters.status || filters.statuses.length > 0,
    filters.adminId,
    filters.createdFrom || filters.createdTo,
].filter(hasActiveValue).length);

export const OrderList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const orderFilters = useMemo(() => parseOrderListFilters(location.search), [location.search]);
    const activeFilterCount = useMemo(() => countActiveOrderFilters(orderFilters), [orderFilters]);

    const [loading, setLoading] = useState(false);
    const [showExport, setShowExport] = useState(false);
    const [showFilter, setShowFilter] = useState(false);

    const [orders, setOrders] = useState([]);
    const [data, setData] = useState({header: [], rows: []});

    const [tab, setTab] = useState('Todos');
    const [statuses, setStatuses] = useState([]);
    const [orderToDelete, setOrderToDelete] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [totalResults, setTotalResults] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPrevPage, setHasPrevPage] = useState(false);

    const [selectedIds, setSelectedIds] = useState([]);
    const [showConfirmMultipleDelete, setShowConfirmMultipleDelete] = useState(false);
    const [showConfirmMultipleEditStatus, setShowConfirmMultipleEditStatus] = useState(false);

    

    useEffect(() => {
        getStatuses();
    }, []);

    useEffect(() => {
        getOrders();
    }, [location.search]);

    useEffect(() => {
        generateData();
    }, [orders, location.search, tab]);

    const getStatuses = () => {
        api.get(`/status?filters[0][type][equal]=order`).then(res => {
            setStatuses(res.data.resources);
        });
    }

    const getOrders = () => {
        setLoading(true);
        api.get(`/order/paginate?${buildOrderApiQuery(orderFilters)}`).then(res => {
            setOrders(res.data.resources.data);
            setCurrentPage(res.data.resources.pagination.current_page);
            setLastPage(res.data.resources.pagination.last_page)
            setPerPage(res.data.resources.pagination.per_page)
            setTotalResults(res.data.resources.pagination.total)
            setHasNextPage(res.data.resources.pagination.next_page_url !== null);
            setHasPrevPage(res.data.resources.pagination.prev_page_url !== null);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    }

    const generateData = () => {
        let header = ['Código', 'Nome', 'Contato', 'Responsável', 'Status', 'Orçamento', 'Total', 'Data da emissão'];
        const rows = [];

        // if(hasPermission('view_margem_order')){
        //     header.push('Dif. de margem');
        // }

        // header.push('Conferência');

        if(hasPermission('delete_order')){
            header.push('');
        }
        
        
        orders.map(order => {
            let buttons = [];
            const returnTo = encodeURIComponent(`/order/list${location.search}`);
            let data = [
                order.code,
                order?.customer_company_trade_name,
                order?.customer_name,
                <UserCell name={order?.admin_name} photo={order?.admin_photo_url} />,
                <>
                    <DotStatus color={order?.status_color} size={15} />
                    {order?.status_name}
                </>,
                order.budget_id ? <Link target={`_blank`} to={`/quote/edit/${order.budget_id}`}>{order.budget_code}</Link> : '',
                `R$ ${convertIntToMoney(order?.total)}`,
                moment(order.created_at).format('DD/MM/YYYY HH:mm'),
            ];

            // if(hasPermission('view_margem_order')){
            //     data.push(
            //         <span className={`indicator ${order?.margem_real >= order?.margem_orcamento ? 'positive' : 'negative'}`}>
            //             {convertIntToMoney(order?.margem_dif, true) }
            //         </span>
            //     );
            // }
            // data.push(countOrderPendingConferencesTag(order));
            if(hasPermission('delete_order')){
                buttons.push(<Button type={`delete`} svg={`delete`} action={() => setOrderToDelete(order.id)} />)
            }
            rows.push({
                data,
                to: `/order/edit/${order?.id}?return_to=${returnTo}`,
                buttons,
                id: order.id,
            });
        });

        setData({header, rows});
    }

    const deleteOrder = () => {
        setLoading(true);

        api.delete(`/order/${orderToDelete}`).then(res => {
            getOrders();
            setOrderToDelete(null);
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    const deleteMultipleOrders = () => {
        setLoading(true);

        api.delete(`/order/multiple`, {
            data: {
                ids: selectedIds
            }
        }).then(res => {
            setSelectedIds([]);
            getOrders();
            setShowConfirmMultipleDelete(false);
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    const editMultipleOrders = (statusId) => {
        setLoading(true);
        api.put(`/order/multipleUpdate`, {
            ids: selectedIds,
            status_id: statusId,
        }).then(res => {
            toast.success('Pedido atualizado com sucesso');
            setSelectedIds([]);
            getOrders();
            setShowConfirmMultipleEditStatus(false);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    }
    

    const updateOrderListUrl = (changes, resetPage = true) => {
        const nextFilters = {
            ...orderFilters,
            ...changes,
            page: resetPage ? 1 : (changes.page || orderFilters.page),
        };
        const search = buildOrderListSearch(nextFilters);

        navigate({
            pathname: location.pathname,
            search: search ? `?${search}` : '',
        });
    }

    const handleSetFilter = (value, metadata) => {
        updateOrderListUrl({
            searchField: metadata?.field || orderFilters.searchField,
            searchOperation: metadata?.operation || orderFilters.searchOperation,
            search: metadata?.value ?? '',
        });
    }

    const handleSetCustomFilters = value => {
        updateOrderListUrl({
            adminId: value.adminId,
            createdFrom: value.createdFrom,
            createdTo: value.createdTo,
        });
    }

    const handleSetStatus = statusId => {
        updateOrderListUrl({ status: statusId, statuses: [] });
    }

    const handleSetPage = page => {
        updateOrderListUrl({ page }, false);
    }

    return (
        <>
            <ModalExport
                showModal={showExport}
                setShowModal={setShowExport}
                url={`${process.env.REACT_APP_URL}/order/export`}
            />
            <ModalDelete
                show={showConfirmMultipleDelete}
                text={<p>Tem certeza que deseja apagar <strong>{selectedIds.length} {selectedIds.length > 1 ? 'pedidos' : 'pedido'}</strong>?</p>}
                action={deleteMultipleOrders}
                loading={loading}
                close={() => setShowConfirmMultipleDelete(false)}
            />
            <ModalDelete
                show={orderToDelete !== null}
                action={deleteOrder}
                loading={loading}
                close={() => setOrderToDelete(null)}
            />
            <ModalEditStatus
                show={showConfirmMultipleEditStatus}
                action={editMultipleOrders}
                loading={loading}
                close={() => setShowConfirmMultipleEditStatus(false)}
                type="order"
            />            
            <ModalFilter
                showFilter={showFilter}
                setShowFilter={setShowFilter}
                filters={orderFilters}
                setCustomFilters={handleSetCustomFilters}
            />
            <PanelTemplate id="order-list">
                <PageHeader
                    left={
                        <FilterSimple 
                            params={orderSearchParams}
                            filter={handleSetFilter}
                            externalValue={orderFilters.search}
                            externalOption={orderFilters.searchField}
                        />
                    }

                    right={
                        <>
                            {hasPermission('delete_order') && selectedIds.length > 0 &&
                                <Button 
                                type={`secondary`} 
                                size={`small`} 
                                svg={`delete`}
                                text={`Excluir ${selectedIds.length} selecionados`} 
                                action={() => setShowConfirmMultipleDelete(true)}  
                                />
                            }
                            {selectedIds.length > 0 &&
                                <Button 
                                type={`secondary`} 
                                size={`small`} 
                                svg={`edit`}
                                text={`Editar status de ${selectedIds.length} selecionados`} 
                                action={() => setShowConfirmMultipleEditStatus(true)}  
                                />
                            }
                            {hasPermission('export_order') &&
                                <Button 
                                type={`secondary`} 
                                size={`small`} 
                                text={`Exportar pedidos`} 
                                action={() => setShowExport(true)}  
                                />
                            }
                            <Button type={`secondary`} size={`small`} text={`Filtrar`} svg="filter" badge={activeFilterCount} action={() => setShowFilter(true)} />
                        </>
                    }
                />
                <ul className="filter-tab">
                    <li>
                        <a href="#" className={orderFilters.status === null && orderFilters.statuses.length === 0 ? 'active' : ''} onClick={e => {
                        e.preventDefault();
                        handleSetStatus(null);
                    }}>Todos</a></li>
                    {statuses.map((status, index) => (
                        <li key={index}>
                            <a href="#" className={orderFilters.statuses.length === 0 && orderFilters.status === status.id ? 'active' : ''} onClick={e => {
                                e.preventDefault();
                                handleSetStatus(status.id);
                            }}>
                                {status.name}
                            </a>
                        </li>
                    ))}
                </ul>
                <Box className={`no-padding with-pagination with-filter-inline`}>
                    <Table 
                        checks={hasPermission('delete_order')}
                        selectedIds={selectedIds}
                        setSelectedIds={setSelectedIds}
                        header={data.header} 
                        rows={data.rows} 
                        loading={loading}
                    />
                </Box>
                {!loading &&
                    <Pagination
                        currentPage={currentPage}
                        lastPage={lastPage}
                        perPage={perPage}
                        totalResults={totalResults}
                        hasNextPage={hasNextPage}
                        hasPrevPage={hasPrevPage}
                        setCurrentPage={handleSetPage}
                    />
                }
            </PanelTemplate>
        </>
    );
}

export default OrderList;
